import React, { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { SelectValue } from 'antd/es/select';

import './index.less';

import { CheckFieldValueFnMap, useValidationForm } from '../../../../../utils/validationUtil';
import {
  ConnectorModel,
  ELMA365ToolsetAvailability,
  ELMA365ToolsetModel,
  ELMA365VersionAction,
  ELMA365VersionAvailability,
  ELMA365VersionModel,
  ELMA365VersionPhase,
  ELMA365VersionStage,
  ELMA365VersionStatus,
} from '../../../../../../gamma-api';
import { getProblemDetails, tryGetValidationErrors } from '../../../../../utils/errorUtils';
import IbTypography from '../../../../../components/common/IbTypography';
import IbInput from '../../../../../components/common/IbInput';
import IbButton from '../../../../../components/common/IbButton';
import IbModal, { IbModalPosition } from '../../../../../components/common/IbModal';
import IbIcon from '../../../../../components/common/IbIcon';
import ConnectorsModal from '../../../../../components/ConnectorsModal';
import { defaultApi } from '../../../../../apis';
import { LARGE_PAGE_SIZE } from '../../../../../constants';
import { alertsSelectorAdd, currentFolderIdSelector } from '../../../../../recoil';
import IbSelect from '../../../../../components/common/IbSelect';
import VersionStatus from '../VersionStatus';
import NamespaceTable from '../NamespaceTable';
import ApplicationTable from '../ApplicationTable';
import ProcessTable from '../ProcessTable';
import ToolTable from '../ToolTable';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import AuthSession from '../../../../../components/AuthSession';
import { AuthSessionStatus, IAuthSessionState } from '../../../../../components/AuthSession/types';
import { isAuthChallenged, isAuthCompleted, isAuthPending } from '../../../../../components/AuthSession/utils';
import { fixObjectId } from '../../../../../utils/idUtils';

import {
  CONNECTORS_BUTTON_CLASS_NAME,
  DEFAULT_MODAL_WIDTH,
  EMPTY_APPENDING_VERSION,
  EMPTY_CREATION_VERSION,
  EMPTY_TOOLSET,
  FORM_ITEM_CLASS_NAME,
  INITIAL_MODAL_WIDTH,
  MAIN_CLASS_NAME,
} from './const';

type ToolsetForm = {
  name: string;
  connector_id: string;
};

const checkFieldValueFnMap: CheckFieldValueFnMap<ToolsetForm> = {
  name: (value: string) => !!(value || '').trim(),
  connector_id: (value: string) => !!value,
};

interface IToolsetModalProps {
  visible: boolean;
  action?: ELMA365VersionAction;
  editToolset?: ELMA365ToolsetModel;
  onFinish: (toolset: ELMA365ToolsetModel) => void;
  onCancel: () => void;
}

const ToolsetModal: React.FC<IToolsetModalProps> = ({ visible, action, editToolset, onFinish, onCancel }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const folderId = useRecoilValue(currentFolderIdSelector);

  const [connectorsModalVisible, setConnectorsModalVisible] = useState(false);
  const [connectorsSelectLoading, setConnectorsSelectLoading] = useState(true);

  const [namespacesVersionId, setNamespacesVersionId] = useState<string>();
  const [toggledNamespaceIds, setToggledNamespaceIds] = useState<{ [key: string]: boolean }>({});

  const [applicationsVersionId, setApplicationsVersionId] = useState<string>();
  const [toggledApplicationIds, setToggledApplicationIds] = useState<{ [key: string]: boolean }>({});

  const [processesVersionId, setProcessesVersionId] = useState<string>();
  const [toggledProcessIds, setToggledProcessIds] = useState<{ [key: string]: boolean }>({});

  const [toolsVersionId, setToolsVersionId] = useState<string>();
  const [toggledToolIds, setToggledToolIds] = useState<{ [key: string]: boolean }>({});

  const emptyVersion = action === ELMA365VersionAction.Appending ? EMPTY_APPENDING_VERSION : EMPTY_CREATION_VERSION;
  const [toolset, setToolset] = useState<ELMA365ToolsetModel>(EMPTY_TOOLSET);
  const [version, setVersion] = useState<ELMA365VersionModel>(emptyVersion);
  const [saving, setSaving] = useState(false);

  const [connectors, setConnectors] = useState<ConnectorModel[]>([]);
  const [authState, setAuthState] = useState<IAuthSessionState>({ status: AuthSessionStatus.Unknown });

  const initialFormValues: ToolsetForm = {
    name: editToolset?.name || '',
    connector_id: fixObjectId(editToolset?.connector_id) || '',
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    reset,
    validate,
    getShowError,
    getErrorMessage,
    onSubmitErrors,
  } = useValidationForm<ToolsetForm>(initialFormValues, checkFieldValueFnMap);

  const translate = getTranslateFunc('Toolkits: ELMA365: Toolset:');

  const handleServerError = (error: unknown, newVersion: ELMA365VersionModel) => {
    const validationErrors =
      newVersion.stage === ELMA365VersionStage.Connector ? tryGetValidationErrors(error as Error) : undefined;
    if (validationErrors) {
      onSubmitErrors(validationErrors);
      setVersion({ ...newVersion, status: ELMA365VersionStatus.Pending });
      return;
    }

    // eslint-disable-next-line no-console
    console.error(error);
    const problemDetails = getProblemDetails(error);
    setVersion({
      ...newVersion,
      status: ELMA365VersionStatus.Problem,
      problem: { details: problemDetails },
    });
  };

  const loadConnectors = async () => {
    setConnectorsSelectLoading(true);

    try {
      const response = await defaultApi.searchConnectors(0, LARGE_PAGE_SIZE, false);
      setConnectors(response.data.items || []);
    } catch (err) {
      addAlert({
        content: translate('Connectors: Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    setConnectorsSelectLoading(false);
  };

  const onVisiblePropChange = () => {
    if (!visible) {
      return;
    }

    loadConnectors().finally();
    reset();

    setToolset(editToolset || EMPTY_TOOLSET);
    setVersion(emptyVersion);
    setAuthState({ status: AuthSessionStatus.Unknown });
  };

  useEffect(onVisiblePropChange, [visible]);

  const onNameChange = (value: string) => {
    setToolset({ ...toolset, name: value });
    onFieldChange('name', value);
  };

  const onNameBlur = () => onFieldBlur('name');

  const onConnectorChange = (value: SelectValue) => {
    const connectorId = value as string;
    setToolset({ ...toolset, connector_id: connectorId });
    onFieldChange('connector_id', connectorId);
  };

  const onConnectorBlur = () => onFieldBlur('connector_id');

  const onSetupConnectorsClick = () => {
    setConnectorsModalVisible(true);
  };

  const onConnectorsModalClose = async () => {
    setConnectorsModalVisible(false);
    await loadConnectors();
  };

  const onNamespaceToggle = (namespaceId: string, selected: boolean) => {
    setToggledNamespaceIds({ ...toggledNamespaceIds, [namespaceId]: selected });
  };

  const onApplicationToggle = (applicationId: string, selected: boolean) => {
    setToggledApplicationIds({ ...toggledApplicationIds, [applicationId]: selected });
  };

  const onProcessToggle = (processId: string, selected: boolean) => {
    setToggledProcessIds({ ...toggledProcessIds, [processId]: selected });
  };

  const onToolToggle = (toolId: string, selected: boolean) => {
    setToggledToolIds({ ...toggledToolIds, [toolId]: selected });
  };

  const stepToNamespaces = async () => {
    if (!action || !validate()) {
      return;
    }

    let newToolset: ELMA365ToolsetModel;
    let newVersion: ELMA365VersionModel;
    if (version.stage === ELMA365VersionStage.Connector) {
      setVersion({
        ...version,
        status: ELMA365VersionStatus.Processing,
        phase: ELMA365VersionPhase.Selection,
      });

      if (toolset.toolset_id) {
        try {
          const versionResponse = await defaultApi.createELMA365ToolkitVersion(
            {
              folder_id: folderId,
              connector_id: toolset.connector_id,
              toolset_id: toolset.toolset_id,
              action,
              availability: ELMA365VersionAvailability.Enabled,
              name: toolset.name,
            },
            authState.options
          );
          newToolset = toolset;
          newVersion = versionResponse.data.version;
        } catch (error) {
          handleServerError(error, version);
          return;
        }
      } else {
        try {
          const toolsetResponse = await defaultApi.createELMA365ToolkitToolset(
            {
              folder_id: folderId,
              connector_id: toolset.connector_id,
              availability: ELMA365ToolsetAvailability.Enabled,
              name: toolset.name,
            },
            authState.options
          );
          newToolset = toolsetResponse.data.toolset;
          newVersion = toolsetResponse.data.version;
        } catch (error) {
          handleServerError(error, version);
          return;
        }
      }

      if (newVersion.status === ELMA365VersionStatus.Problem) {
        setToolset(newToolset);
        setVersion(newVersion);
        return;
      }
    } else {
      newToolset = toolset;
      newVersion = version;
    }

    newVersion = {
      ...newVersion,
      status: ELMA365VersionStatus.Processing,
      stage: ELMA365VersionStage.Namespaces,
      phase: ELMA365VersionPhase.Obtaining,
    };

    setNamespacesVersionId(undefined);
    setToggledNamespaceIds({});
    setToolset(newToolset);
    setVersion(newVersion);

    try {
      const namespaceResponse = await defaultApi.obtainELMA365ToolkitNamespaces(
        {
          version_id: newVersion.version_id,
        },
        authState.options
      );
      setNamespacesVersionId(newVersion.version_id);
      setVersion(namespaceResponse.data.version);
    } catch (error) {
      handleServerError(error, newVersion);
    }
  };

  const stepToApplications = async () => {
    let newVersion: ELMA365VersionModel;
    if (version.stage === ELMA365VersionStage.Namespaces) {
      setVersion({
        ...version,
        status: ELMA365VersionStatus.Processing,
        phase: ELMA365VersionPhase.Selection,
      });
      try {
        const namespaceResponse = await defaultApi.selectELMA365ToolkitNamespaces(
          {
            version_id: version.version_id,
            namespace_ids: toggledNamespaceIds,
          },
          authState.options
        );
        newVersion = namespaceResponse.data.version;
      } catch (error) {
        handleServerError(error, version);
        return;
      }

      if (newVersion.status === ELMA365VersionStatus.Problem) {
        setVersion(newVersion);
        return;
      }
    } else {
      newVersion = version;
    }

    newVersion = {
      ...newVersion,
      status: ELMA365VersionStatus.Processing,
      stage: ELMA365VersionStage.Applications,
      phase: ELMA365VersionPhase.Obtaining,
    };

    setApplicationsVersionId(undefined);
    setToggledApplicationIds({});
    setVersion(newVersion);

    try {
      const applicationResponse = await defaultApi.obtainELMA365ToolkitApplications(
        {
          version_id: newVersion.version_id,
        },
        authState.options
      );
      setApplicationsVersionId(newVersion.version_id);
      setVersion(applicationResponse.data.version);
    } catch (error) {
      handleServerError(error, newVersion);
    }
  };

  const stepToProcesses = async () => {
    let newVersion: ELMA365VersionModel;
    if (version.stage === ELMA365VersionStage.Applications) {
      setVersion({
        ...version,
        status: ELMA365VersionStatus.Processing,
        phase: ELMA365VersionPhase.Selection,
      });
      try {
        const applicationResponse = await defaultApi.selectELMA365ToolkitApplications(
          {
            version_id: version.version_id,
            application_ids: toggledApplicationIds,
          },
          authState.options
        );
        newVersion = applicationResponse.data.version;
      } catch (error) {
        handleServerError(error, version);
        return;
      }

      if (newVersion.status === ELMA365VersionStatus.Problem) {
        setVersion(newVersion);
        return;
      }
    } else {
      newVersion = version;
    }

    newVersion = {
      ...newVersion,
      status: ELMA365VersionStatus.Processing,
      stage: ELMA365VersionStage.Processes,
      phase: ELMA365VersionPhase.Obtaining,
    };

    setProcessesVersionId(undefined);
    setToggledProcessIds({});
    setVersion(newVersion);

    try {
      const processResponse = await defaultApi.obtainELMA365ToolkitProcesses(
        {
          version_id: newVersion.version_id,
        },
        authState.options
      );
      setProcessesVersionId(newVersion.version_id);
      setVersion(processResponse.data.version);
    } catch (error) {
      handleServerError(error, newVersion);
    }
  };

  const stepToTools = async () => {
    let newVersion: ELMA365VersionModel;
    if (version.stage === ELMA365VersionStage.Processes) {
      setVersion({
        ...version,
        status: ELMA365VersionStatus.Processing,
        phase: ELMA365VersionPhase.Selection,
      });
      try {
        const processResponse = await defaultApi.selectELMA365ToolkitProcesses(
          {
            version_id: version.version_id,
            process_ids: toggledProcessIds,
          },
          authState.options
        );
        newVersion = processResponse.data.version;
      } catch (error) {
        handleServerError(error, version);
        return;
      }

      if (newVersion.status === ELMA365VersionStatus.Problem) {
        setVersion(newVersion);
        return;
      }
    } else {
      newVersion = version;
    }

    newVersion = {
      ...newVersion,
      status: ELMA365VersionStatus.Processing,
      stage: ELMA365VersionStage.Tools,
      phase: ELMA365VersionPhase.Obtaining,
    };

    setToolsVersionId(undefined);
    setToggledToolIds({});
    setVersion(newVersion);

    try {
      const toolResponse = await defaultApi.obtainELMA365ToolkitTools(
        {
          version_id: newVersion.version_id,
        },
        authState.options
      );
      setToolsVersionId(newVersion.version_id);
      setVersion(toolResponse.data.version);
    } catch (error) {
      handleServerError(error, newVersion);
    }
  };

  const onAuthStateChange = () => {
    if (action !== ELMA365VersionAction.Appending && !authState.event) {
      return;
    }

    if (isAuthChallenged(authState)) {
      setAuthState({ ...authState, status: AuthSessionStatus.Redirection });
    } else if (isAuthCompleted(authState)) {
      stepToNamespaces().finally();
    }
  };

  useEffect(onAuthStateChange, [authState]);

  const onNextButtonClick = async () => {
    if (version.stage === ELMA365VersionStage.Connector) {
      if (isAuthChallenged(authState)) {
        setAuthState({ ...authState, status: AuthSessionStatus.Redirection });
      } else {
        await stepToNamespaces();
      }
    } else if (version.stage === ELMA365VersionStage.Namespaces) {
      if (version.phase === ELMA365VersionPhase.Obtaining) {
        await stepToNamespaces();
      } else {
        await stepToApplications();
      }
    } else if (version.stage === ELMA365VersionStage.Applications) {
      if (version.phase === ELMA365VersionPhase.Obtaining) {
        await stepToApplications();
      } else {
        await stepToProcesses();
      }
    } else if (version.stage === ELMA365VersionStage.Processes) {
      if (version.phase === ELMA365VersionPhase.Obtaining) {
        await stepToProcesses();
      } else {
        await stepToTools();
      }
    } else {
      await stepToTools();
    }
  };

  const onBackButtonClick = () => {
    if (version.stage === ELMA365VersionStage.Namespaces) {
      setVersion({
        ...version,
        status: ELMA365VersionStatus.Empty,
        stage: ELMA365VersionStage.Connector,
        phase: ELMA365VersionPhase.Pending,
      });
    } else if (version.stage === ELMA365VersionStage.Applications) {
      setVersion({
        ...version,
        status: ELMA365VersionStatus.Pending,
        stage: ELMA365VersionStage.Namespaces,
        phase: ELMA365VersionPhase.Pending,
      });
    } else if (version.stage === ELMA365VersionStage.Processes) {
      setVersion({
        ...version,
        status: ELMA365VersionStatus.Pending,
        stage: ELMA365VersionStage.Applications,
        phase: ELMA365VersionPhase.Pending,
      });
    } else if (version.stage === ELMA365VersionStage.Tools) {
      setVersion({
        ...version,
        status: ELMA365VersionStatus.Pending,
        stage: ELMA365VersionStage.Processes,
        phase: ELMA365VersionPhase.Pending,
      });
    }
  };

  const onCancelButtonClick = () => {
    onCancel?.();
  };

  const onUpdateButtonClick = async () => {
    if (!validate()) {
      return;
    }

    let newToolset = toolset;
    setSaving(true);

    try {
      const toolsetResponse = await defaultApi.updateELMA365ToolkitToolset(toolset.toolset_id, {
        connector_id: toolset.connector_id,
        name: toolset.name,
      });
      if (toolsetResponse.data.toolset) {
        newToolset = toolsetResponse.data.toolset;
        setToolset(newToolset);
      }
    } catch (error) {
      addAlert({ content: translate('Failed to update toolset'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(error);
      return;
    } finally {
      setSaving(false);
    }

    onFinish?.(newToolset);
  };

  const onSelectButtonClick = async () => {
    let newVersion = version;
    let newToolset = toolset;

    setVersion({
      ...version,
      status: ELMA365VersionStatus.Processing,
      phase: ELMA365VersionPhase.Selection,
    });
    setSaving(true);

    try {
      const toolResponse = await defaultApi.selectELMA365ToolkitTools(
        {
          version_id: version.version_id,
          component_keys: {},
          tool_ids: toggledToolIds,
        },
        authState.options
      );

      newVersion = toolResponse.data.version;
      setVersion(newVersion);

      if (toolResponse.data.toolset) {
        newToolset = toolResponse.data.toolset;
        setToolset(newToolset);
      }

      if (toolResponse.data.version.status === ELMA365VersionStatus.Problem) {
        return;
      }
    } catch (error) {
      handleServerError(error, version);
      return;
    } finally {
      setSaving(false);
    }

    onFinish?.(newToolset);
  };

  const onRestartButtonClick = async () => {
    if (version.stage !== ELMA365VersionStage.Tools || version.phase === ELMA365VersionPhase.Obtaining) {
      await onNextButtonClick();
    } else {
      await onSelectButtonClick();
    }
  };

  const onAuthChange = async (newState: IAuthSessionState) => {
    setAuthState(newState);
  };

  const renderTitle = () => {
    if (!action) {
      return translate('Title: Updating');
    }

    if (action === ELMA365VersionAction.Appending) {
      return translate('Title: Appending');
    }

    if (version.stage === ELMA365VersionStage.Connector) {
      return translate('Title: Connector');
    }

    if (version.stage === ELMA365VersionStage.Namespaces) {
      return translate('Title: Namespaces');
    }

    if (version.stage === ELMA365VersionStage.Applications) {
      return translate('Title: Applications');
    }

    if (version.stage === ELMA365VersionStage.Processes) {
      return translate('Title: Processes');
    }

    if (version.stage === ELMA365VersionStage.Tools) {
      return translate('Title: Tools');
    }

    return '';
  };

  const renderStatus = () => {
    return (
      <VersionStatus
        problem={version.problem}
        stage={version.stage}
        status={version.status}
        onRestart={onRestartButtonClick}
      />
    );
  };

  const renderConnector = () => {
    const connectorOptions = connectors.map((connector) => ({
      value: connector.connector_id,
      label: `${connector.name} - ${connector.settings.apis.web.url}`,
    }));

    return (
      <div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{translate('Name: Title')}</IbTypography.Paragraph>
          <IbInput
            disabled={action === ELMA365VersionAction.Appending}
            status={getShowError(validationState.name) ? 'error' : 'default'}
            value={toolset.name}
            onBlur={onNameBlur}
            onChange={onNameChange}
          />
          {getShowError(validationState.name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.name, translate('Name: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{translate('Connector: Title')}</IbTypography.Paragraph>
          <IbSelect
            disabled={action === ELMA365VersionAction.Appending}
            loading={connectorsSelectLoading}
            options={connectorOptions}
            status={getShowError(validationState.connector_id) ? 'error' : 'default'}
            value={fixObjectId(toolset.connector_id)}
            onBlur={onConnectorBlur}
            onChange={onConnectorChange}
          />
          {getShowError(validationState.connector_id) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.connector_id, translate('Connector: Error'))}
            </IbTypography.Paragraph>
          )}
          <IbButton
            className={CONNECTORS_BUTTON_CLASS_NAME}
            icon={<IbIcon iconName="setting-two" />}
            type="link"
            onClick={onSetupConnectorsClick}
          >
            {translate('Setup connectors')}
          </IbButton>
        </div>
        <ConnectorsModal visible={connectorsModalVisible} onClose={onConnectorsModalClose} />
      </div>
    );
  };

  const renderNamespaces = () => {
    if (!action) {
      return null;
    }

    return (
      <NamespaceTable
        action={action}
        toggledIds={toggledNamespaceIds}
        toolset={toolset}
        versionId={namespacesVersionId}
        onToggle={onNamespaceToggle}
      />
    );
  };

  const renderApplications = () => {
    if (!action) {
      return null;
    }

    return (
      <ApplicationTable
        action={action}
        toggledIds={toggledApplicationIds}
        toolset={toolset}
        versionId={applicationsVersionId}
        onToggle={onApplicationToggle}
      />
    );
  };

  const renderProcesses = () => {
    if (!action) {
      return null;
    }

    return (
      <ProcessTable
        action={action}
        toggledIds={toggledProcessIds}
        toolset={toolset}
        versionId={processesVersionId}
        onToggle={onProcessToggle}
      />
    );
  };

  const renderTools = () => {
    return <ToolTable toggledIds={toggledToolIds} versionId={toolsVersionId} onToggle={onToolToggle} />;
  };

  const renderValue = () => {
    if (version.stage === ELMA365VersionStage.Connector) {
      return renderConnector();
    } else if (version.stage === ELMA365VersionStage.Namespaces) {
      return renderNamespaces();
    } else if (version.stage === ELMA365VersionStage.Applications) {
      return renderApplications();
    } else if (version.stage === ELMA365VersionStage.Processes) {
      return renderProcesses();
    } else if (version.stage === ELMA365VersionStage.Tools) {
      return renderTools();
    }
  };

  const renderAuth = () => {
    return <AuthSession connectorId={toolset?.connector_id} status={authState.status} onChange={onAuthChange} />;
  };

  const renderContent = () => {
    if (!action) {
      return renderConnector();
    }

    return (
      <>
        {renderStatus()}
        {renderValue()}
        {renderAuth()}
      </>
    );
  };

  const renderFooter = () => {
    const buttons = [];

    if (!action) {
      buttons.push(
        <IbButton key="finish" disabled={saving} type="primary" onClick={onUpdateButtonClick}>
          {translate('Update')}
        </IbButton>
      );
      buttons.push(
        <IbButton key="cancel" disabled={saving} type="secondary" onClick={onCancelButtonClick}>
          {translate('Cancel')}
        </IbButton>
      );
      return buttons;
    }

    const processing = saving || version.status === ELMA365VersionStatus.Processing;
    const problem = version.status === ELMA365VersionStatus.Problem;

    if (version.stage === ELMA365VersionStage.Tools) {
      buttons.push(
        <IbButton key="finish" disabled={processing || problem} type="primary" onClick={onSelectButtonClick}>
          {translate(toolset.version_id === version.version_id ? 'Create' : 'Replace')}
        </IbButton>
      );
    } else {
      buttons.push(
        <IbButton
          key="next"
          disabled={processing || problem || isAuthPending(authState)}
          type="primary"
          onClick={onNextButtonClick}
        >
          {translate('Next')}
        </IbButton>
      );
    }

    if (
      version.stage === ELMA365VersionStage.Connector ||
      (action === ELMA365VersionAction.Appending && version.stage === ELMA365VersionStage.Namespaces)
    ) {
      buttons.push(
        <IbButton key="cancel" disabled={processing} type="secondary" onClick={onCancelButtonClick}>
          {translate('Cancel')}
        </IbButton>
      );
    } else {
      buttons.push(
        <IbButton key="back" disabled={processing} type="secondary" onClick={onBackButtonClick}>
          {translate('Back')}
        </IbButton>
      );
    }

    return buttons;
  };

  const modalPosition =
    version.stage === ELMA365VersionStage.Connector ? IbModalPosition.Centered : IbModalPosition.FullHeight;
  const modalWidth = version.stage === ELMA365VersionStage.Connector ? INITIAL_MODAL_WIDTH : DEFAULT_MODAL_WIDTH;

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      loading={saving}
      position={modalPosition}
      title={renderTitle()}
      visible={visible}
      width={modalWidth}
      onCancel={onCancel}
    >
      {renderContent()}
    </IbModal>
  );
};

export default ToolsetModal;
